import { makeAuthenticatedRequest } from './api';

/**
 * Upload a police certificate to the dedicated endpoint.
 * The server will attempt to extract the issue date from the PDF
 * and return the computed expiry date (issue + 3 years).
 * @param {File} file
 * @returns {Promise<{url: string, issueDate: string|null, expiryDate: string|null}>}
 */
export const uploadCompliancePoliceCertificate = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  // makeAuthenticatedRequest returns responseData.data, which is { url, issueDate, expiryDate }
  return makeAuthenticatedRequest('/fse/compliance/upload-police-certificate', {
    method: 'POST',
    body: formData,
  });
};

/**
 * Upload a single file to the server
 * @param {Object} fileObj - File object with file and category
 * @returns {Promise<Object>} Response with uploaded file URLs
 */
export const uploadSingleFile = async (fileObj) => {
  console.log('uploadSingleFile called with:', fileObj);
  console.log('fileObj.file:', fileObj.file);
  console.log('fileObj.category:', fileObj.category);
  
  if (!fileObj.file) {
    throw new Error('File object is missing');
  }
  
  const formData = new FormData();
  // Use 'files' (plural) as the field name to match the server expectation
  formData.append('files', fileObj.file);
  formData.append('category', fileObj.category);

  // Debug FormData contents
  console.log('FormData entries:');
  for (let [key, value] of formData.entries()) {
    console.log(key, value);
  }

  try {
    const response = await makeAuthenticatedRequest('/upload/files', {
      method: 'POST',
      body: formData,
      // Don't set Content-Type header - let browser set it with boundary for FormData
    });

    return response;
  } catch (error) {
    console.error('File upload error:', error);
    throw new Error(error.message || 'Failed to upload file');
  }
};

/**
 * Upload multiple files to the server (each file uploaded individually with its own category)
 * @param {Array} files - Array of file objects with file and category
 * @returns {Promise<Object>} Combined response with all uploaded file URLs
 */
export const uploadFiles = async (files) => {
  if (!files || files.length === 0) {
    throw new Error('No files to upload');
  }

  console.log('uploadFiles: Starting upload for', files.length, 'files');

  const uploadPromises = files.map(fileObj => uploadSingleFile(fileObj));
  
  try {
    const responses = await Promise.all(uploadPromises);
    console.log('uploadFiles: All responses received:', responses);
    
    // Combine all URLs from all responses
    const allUrls = responses.reduce((acc, response) => {
      console.log('uploadFiles: Processing response:', response);
      // Check both response.data.urls (nested) and response.urls (direct)
      const urls = response.data?.urls || response.urls;
      if (urls && Array.isArray(urls)) {
        console.log('uploadFiles: Found URLs:', urls);
        acc.push(...urls);
      } else {
        console.log('uploadFiles: No URLs found in response data');
      }
      return acc;
    }, []);

    console.log('uploadFiles: Combined URLs:', allUrls);

    const finalResponse = {
      status: 'success',
      message: `${allUrls.length} file(s) uploaded successfully.`,
      data: {
        urls: allUrls
      }
    };

    console.log('uploadFiles: Final response:', finalResponse);
    return finalResponse;
  } catch (error) {
    console.error('File upload error:', error);
    throw new Error(error.message || 'Failed to upload files');
  }
};

/**
 * Delete a file from the server
 * @param {string} fileUrl - URL of the file to delete
 * @returns {Promise<Object>} Response with deletion status
 */
export const deleteFile = async (fileUrl) => {
  if (!fileUrl) {
    throw new Error('File URL is required');
  }

  try {
    const response = await makeAuthenticatedRequest('/upload/delete-file', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fileUrl: fileUrl
      }),
    });

    return response;
  } catch (error) {
    console.error('File deletion error:', error);
    throw new Error(error.message || 'Failed to delete file');
  }
};

/**
 * Get a fresh short-lived signed URL for an existing GCS document.
 * Avoids SignatureDoesNotMatch errors from stale stored signed URLs.
 * @param {string} fileUrl - Stored file URL or GCS object path
 * @returns {Promise<string>} Fresh signed URL valid for 60 minutes
 */
export const getSignedUrl = async (fileUrl) => {
  if (!fileUrl) {
    throw new Error('File URL is required');
  }

  const response = await makeAuthenticatedRequest('/upload/signed-url', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ fileUrl }),
  });

  if (response.signedUrl) {
    return response.signedUrl;
  }
  throw new Error(response.message || 'Failed to get signed URL');
};
